import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Calendar, Send } from "lucide-react";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const interests = [
  "1:1 Coaching for Adults / College Students",
  "1:1 Coaching for Parents",
  "Group Training",
  "Not sure yet, just exploring",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", interest: interests[0], message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you! Your message is on its way to Noa.");
      setForm({ name: "", email: "", interest: interests[0], message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your message. Please email noagrantcoaching@gmail.com.";
      toast.error(typeof msg === "string" ? msg : "Could not send message.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5 reveal">
          <span className="text-xs md:text-sm uppercase tracking-[0.28em] text-brand-coral font-semibold">
            Let's talk
          </span>
          <h2 className="mt-5 font-serif text-4xl md:text-5xl lg:text-6xl text-brand-navy leading-[1.05] tracking-tight">
            Reach out, or pick a time.
          </h2>
          <p className="mt-6 text-lg text-brand-navy/75 leading-relaxed">
            The first conversation is free, friendly, and zero-pressure. Tell me
            what's going on, or just hop on the calendar.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href={SITE.calendly}
              target="_blank"
              rel="noreferrer noopener"
              data-testid="contact-calendly-link"
              className="group flex items-center gap-4 rounded-2xl bg-white border border-brand-sage/20 p-5 hover:border-brand-coral hover:shadow-[0_12px_30px_rgba(47,66,80,0.06)] transition"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-coral/10 text-brand-coral flex items-center justify-center group-hover:bg-brand-coral group-hover:text-brand-cream transition">
                <Calendar size={20} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-sage font-semibold">
                  Schedule
                </div>
                <div className="font-serif text-xl text-brand-navy">
                  Book a Free Discovery Call
                </div>
              </div>
            </a>

            <a
              href={`mailto:${SITE.email}`}
              data-testid="contact-email-link"
              className="group flex items-center gap-4 rounded-2xl bg-white border border-brand-sage/20 p-5 hover:border-brand-coral hover:shadow-[0_12px_30px_rgba(47,66,80,0.06)] transition"
            >
              <div className="w-12 h-12 rounded-xl bg-brand-coral/10 text-brand-coral flex items-center justify-center group-hover:bg-brand-coral group-hover:text-brand-cream transition">
                <Mail size={20} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-brand-sage font-semibold">
                  Email
                </div>
                <div className="font-serif text-xl text-brand-navy break-all">
                  {SITE.email}
                </div>
              </div>
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 reveal">
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="rounded-3xl bg-white border border-brand-sage/15 p-8 md:p-10 shadow-[0_8px_30px_rgba(47,66,80,0.05)]"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Your name">
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={onChange}
                  data-testid="contact-input-name"
                  className="input-base"
                  placeholder="Jane Doe"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  data-testid="contact-input-email"
                  className="input-base"
                  placeholder="you@email.com"
                />
              </Field>
            </div>

            <div className="mt-5">
              <Field label="What are you interested in?">
                <select
                  name="interest"
                  value={form.interest}
                  onChange={onChange}
                  data-testid="contact-input-interest"
                  className="input-base appearance-none pr-12"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%237c8b84' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 16px center",
                    backgroundSize: "14px 14px",
                  }}
                >
                  {interests.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="mt-5">
              <Field label="Message">
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  data-testid="contact-input-message"
                  className="input-base resize-none"
                  placeholder="Tell me a bit about what's going on..."
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-7 inline-flex items-center justify-center gap-2 rounded-full bg-brand-navy text-brand-cream px-8 py-4 text-sm md:text-base font-medium hover:bg-brand-coral transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending..." : "Send Message"}
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>

      <style>{`
        .input-base {
          width: 100%;
          padding: 14px 16px;
          background: #fff8f5;
          border: 1px solid rgba(124,139,132,0.25);
          border-radius: 14px;
          color: #2f4250;
          font-family: 'Manrope', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color 200ms, box-shadow 200ms, background 200ms;
        }
        .input-base::placeholder { color: rgba(47,66,80,0.4); }
        .input-base:focus {
          border-color: #c7765b;
          background: #ffffff;
          box-shadow: 0 0 0 4px rgba(199,118,91,0.12);
        }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.2em] text-brand-sageDeep font-semibold mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
